/* eslint-disable max-len */
import Relay from 'react-relay/classic';
import {
  RelayNetworkLayer,
  retryMiddleware,
  urlMiddleware,
  gqErrorsMiddleware
} from 'react-relay-network-layer';
import User from 'helper/currentUser';
import RelayLocalSchema from 'relay-local-schema';

import cookie from './cookie';
import { awsEndpoint } from './url';
import refetchTokens from './refetchTokens';
import validateJWT from './validateJWT';
import schema from '../graphql/schema';

const user = new User();

const onLocalNetworkLayerError = (errors, request) => {
  const sanitizedErrors = errors.map(e => ({
    locations: e.locations,
    message: e.message,
    path: e.path,
    stack: e.stack
  }));
  const printedQuery = request._getPrintedQuery();
  const errorObject = {
    type: 'custom_error_track',
    message: 'Error in resolve request locally',
    errors: sanitizedErrors,
    request: printedQuery,
    userId: user.id(),
    time: new Date().toISOString()
  };
  console.log('Error in localNetworkLayer query', errorObject);
  window.bugsnagClient
    && window.bugsnagClient.notify(new Error('error_in_localNetworkLayer_query'), {
      metaData: errorObject
    });

  errors.forEach((error) => {
    window.bugsnagClient && window.bugsnagClient.notify(error);
  });
};
export const relayLocalNetworkLayer = new RelayLocalSchema.NetworkLayer({
  schema,
  onError: onLocalNetworkLayerError
});

// this was necessary as Relay doesn't query again for the course opened once
window.courseRefetchQueries = {};

export default function setupRelayEnvironment() {
  const NetworkLayer = new RelayNetworkLayer(
    [
      urlMiddleware({
        url: awsEndpoint + '/graphql?apiEndpoint=oldbackendapi'
      }),

      gqErrorsMiddleware(),

      retryMiddleware({
        fetchTimeout: 50000,
        retryDelays: attempt => 2 ** (attempt + 4) * 100, // or simple array [3200, 6400, 12800, 25600, 51200, 102400, 204800, 409600],
        forceRetry: (cb, delay) => {
          window.forceRelayRetry = cb;
          console.log('call `forceRelayRetry()` for immediately retry! Or wait ' + delay + ' ms.');
        },
        statusCodes: [500, 503, 504]
      }),

      // example of the custom inline middleware (add `X-Request-ID` to request headers)
      next => async (req) => {
        if (user.isMasqueraded()) {
          req.headers.masquerade = JSON.stringify({ _id: user.id() });
          req.headers.user = JSON.stringify({ _id: user.id() });
        }

        let lyearnToken = cookie.get('lyearn_token');
        const isValid = validateJWT(lyearnToken);
        if (!isValid) {
          await refetchTokens();
        }
        lyearnToken = cookie.get('lyearn_token');
        req.headers.lyearn_token = lyearnToken;

        if (!req.headers.lyearn_token) {
          window.bugsnagClient
            && window.bugsnagClient.notify(new Error('lyearn_token_not_found'), {
              metaData: {
                type: 'custom_error_track',
                message: 'lyearn_token_not_found',
                requestObject: req,
                cookies: document.cookie,
                userId: user.id(),
                time: new Date().toISOString()
              }
            });
        }

        return next(req);
      }
    ],
    {
      disableBatchQuery: true
    }
  );
  const originalSendMutation = NetworkLayer.sendMutation;
  const originalSendQueries = NetworkLayer.sendQueries;

  NetworkLayer.sendQueries = (queryRequests) => {
    const networkRequests = [];
    const localRequests = [];
    queryRequests.forEach((request) => {
      const query = request.getQuery();
      const { resolveLocally } = query.getVariables();
      if (resolveLocally) {
        localRequests.push(request);
      } else {
        networkRequests.push(request);
      }
    });

    networkRequests.length > 0 && originalSendQueries(networkRequests);
    localRequests.length > 0 && relayLocalNetworkLayer.sendQueries(localRequests);
  };

  const localMutations = [
    'addperformancemutation',
    'addquestionsubmission',
    'updateblockperformancemutation',
    'perquestionsubmission',
    'setprogressmeasureforlearningcardmutation',
    'unsubmitmutation',
    'resubmitassignmentmutation',
    'updatescormmutation'
  ];

  NetworkLayer.sendMutation = (request) => {
    const mutationName = request.getMutation().getName();
    if (localMutations.includes(mutationName.toLowerCase())) {
      relayLocalNetworkLayer.sendMutation(request);
    } else {
      originalSendMutation(request);
    }
  };

  if (!global.relayEnvironment) {
    global.relayEnvironment = new Relay.Environment();
    global.relayEnvironment.injectNetworkLayer(NetworkLayer);
    Relay.Store = global.relayEnvironment;
  }
}
